'use client'

import dynamic from 'next/dynamic'
import { ComponentType, memo, Suspense } from 'react'
import dynamicIconImports from 'lucide-react/dynamicIconImports'

export type IconName = keyof typeof dynamicIconImports

type IconComponent = ComponentType<{ className?: string }>

/**
 * Module-level cache of lazily created icon components. Each icon's
 * `next/dynamic` wrapper is created once, on first use, instead of eagerly
 * building all ~1,500 wrappers at module load.
 */
const iconCache = new Map<IconName, IconComponent>()

const getIcon = (name: IconName): IconComponent | null => {
  const cached = iconCache.get(name)
  if (cached) return cached

  const loader = dynamicIconImports[name]
  if (!loader) return null

  const Icon = dynamic(loader) as IconComponent
  iconCache.set(name, Icon)
  return Icon
}

type DynamicIconProps = {
  name: IconName
  className?: string
}

/**
 * Renders a Lucide icon by name, code-splitting each icon into its own chunk
 * so only icons actually rendered are downloaded. Icons are server-rendered
 * (SSR enabled) to avoid client-side pop-in and layout shift. Unknown icon
 * names render nothing. Lazy chunks are caught here so they do not suspend
 * up to a parent boundary (that remounts the whole page).
 */
const DynamicIcon = memo(({ name, ...props }: DynamicIconProps) => {
  const Icon = getIcon(name)
  if (!Icon) return null
  return (
    <Suspense fallback={null}>
      <Icon {...props} />
    </Suspense>
  )
})
DynamicIcon.displayName = 'DynamicIcon'

export default DynamicIcon
